import { useState, useMemo } from 'react';
import type { WeekType, Location, EmployeeWithSchedule } from '@functieloonhuis/shared';
import { useTeamSchedules, useAllSchedules, useCurrentWeekType, useHolidays } from '../hooks/useWorkSchedules';
import { hasWeekData, getDefaultWeek } from '../utils/weekUtils';
import { getMondayOfWeek, getWorkweekDates, getWorkdaysOfMonth } from '../utils/dateUtils';
import { mapScheduleToWeek, mapScheduleToMonth } from '../utils/planningMapper';
import { WeekToggle } from './WeekToggle';
import { TeamWeekGrid } from './TeamWeekGrid';
import { ScheduleFilters } from './ScheduleFilters';
import { afdelingKey, afdelingOpties } from '../../../lib/afdeling';
import { ViewToggle, type ViewMode } from './ViewToggle';
import { ScopeToggle, type ScopeMode } from './ScopeToggle';
import { ViewModeSelector, type PlanningViewMode } from './ViewModeSelector';
import { DateNavigator } from './DateNavigator';
import { PlanningWeekGrid } from './PlanningWeekGrid';
import { PlanningDayView } from './PlanningDayView';
import { PlanningMonthGrid } from './PlanningMonthGrid';

/** Stable identity — a fresh `[]` per render would defeat every useMemo below. */
const GEEN_MEDEWERKERS: EmployeeWithSchedule[] = [];

export function TeamScheduleOverview() {
  const { data: teamData, isLoading: teamLoading } = useTeamSchedules();
  const { data: allData, isLoading: allLoading } = useAllSchedules();
  const { data: weekData } = useCurrentWeekType();

  // Scope toggle
  const [scope, setScope] = useState<ScopeMode>('team');
  const scopedEmployees = (scope === 'team' ? teamData?.data : allData?.data) ?? GEEN_MEDEWERKERS;
  const isLoading = scope === 'team' ? teamLoading : allLoading;

  // ── Team/afdelingsfilter ──
  // Nodig ook binnen scope 'team': `resolveTeam` levert ALLE afdelingen
  // waarvan iemand manager is, dus "eigen team" kan meerdere teams zijn.
  const [selectedDepartment, setSelectedDepartment] = useState<number | null>(null);

  const departments = useMemo<Array<[number, string]>>(
    () => afdelingOpties(scopedEmployees.map((e) => e.employee)),
    [scopedEmployees],
  );

  // Terugval tijdens het renderen: wisselen van scope (of data die nog moet
  // binnenkomen) kan de gekozen afdeling laten verdwijnen. Een effect zou
  // eerst één frame met een lege lijst tonen; de keuze blijft wél in state,
  // zodat terugwisselen het filter herstelt.
  const effectiveDepartment =
    selectedDepartment !== null && departments.some(([id]) => id === selectedDepartment) ? selectedDepartment : null;

  const employees = useMemo(() => {
    if (effectiveDepartment === null) return scopedEmployees;
    return scopedEmployees.filter(({ employee }) => afdelingKey(employee) === effectiveDepartment);
  }, [scopedEmployees, effectiveDepartment]);

  const currentWeekType = weekData?.data?.currentWeekType;

  // View toggle
  const [viewMode, setViewMode] = useState<ViewMode>('werkrooster');

  // ── Werkdagen (pattern) view state ──
  // Bewust op de ONgefilterde lijst: de Week A/B-toggle moet niet
  // verspringen of verdwijnen zodra je op een team filtert.
  const anyHasWeekB = scopedEmployees.some(emp => hasWeekData(emp.weekB));
  const [selectedWeek, setSelectedWeek] = useState<WeekType | null>(null);
  const effectiveWeek = selectedWeek ?? getDefaultWeek(currentWeekType, anyHasWeekB);

  // ── Werkrooster (planning) view state ──
  const [planningViewMode, setPlanningViewMode] = useState<PlanningViewMode>('week');
  const [currentDate, setCurrentDate] = useState<Date>(() => new Date());

  // Derived dates for each planning view
  const weekStart = useMemo(
    () => getMondayOfWeek(currentDate),
    [currentDate],
  );
  const weekDates = useMemo(() => getWorkweekDates(weekStart), [weekStart]);
  const monthWorkdays = useMemo(
    () => getWorkdaysOfMonth(currentDate.getFullYear(), currentDate.getMonth()),
    [currentDate],
  );

  // Holidays — cover the dates shown
  const yearStart = planningViewMode === 'maand' ? currentDate.getFullYear() : weekStart.getFullYear();
  const yearEnd = planningViewMode === 'week' ? (weekDates[weekDates.length - 1]?.getFullYear() ?? yearStart) : yearStart;
  const { data: holidaysStart } = useHolidays(yearStart);
  const { data: holidaysEnd } = useHolidays(yearEnd);

  const allHolidays = useMemo(() => {
    const items = [...(holidaysStart?.data ?? [])];
    if (yearEnd !== yearStart) items.push(...(holidaysEnd?.data ?? []));
    return items;
  }, [holidaysStart, holidaysEnd, yearStart, yearEnd]);

  // ── Filters (shared) ──
  const [selectedDay, setSelectedDay] = useState<number | null>(null);
  const [selectedLocation, setSelectedLocation] = useState<Location | null>(null);

  // ── Werkdagen view: filter employees ──
  const filteredEmployees = useMemo(() => {
    let result = employees;
    if (selectedLocation) {
      result = result.filter(emp => {
        const weekEntries = effectiveWeek === 'A' ? emp.weekA : emp.weekB;
        return weekEntries.some(day =>
          day.isWorkday && day.location === selectedLocation &&
          (selectedDay ? day.dayOfWeek === selectedDay : true)
        );
      });
    }
    return result;
  }, [employees, selectedLocation, selectedDay, effectiveWeek]);

  // ── Planning view: mapped data ──
  const plannedWeek = useMemo(
    () => mapScheduleToWeek(employees, weekStart, allHolidays),
    [employees, weekStart, allHolidays],
  );
  const plannedMonth = useMemo(
    () => mapScheduleToMonth(employees, currentDate.getFullYear(), currentDate.getMonth(), allHolidays),
    [employees, currentDate, allHolidays],
  );

  const filteredPlannedWeek = useMemo(() => {
    if (!selectedLocation) return plannedWeek;
    return plannedWeek.filter(pw =>
      pw.days.some(day =>
        day.isWorkday && day.location === selectedLocation &&
        (selectedDay ? day.dayOfWeek === selectedDay : true)
      )
    );
  }, [plannedWeek, selectedLocation, selectedDay]);

  const filteredPlannedMonth = useMemo(() => {
    if (!selectedLocation) return plannedMonth;
    return plannedMonth.filter(pw =>
      pw.days.some(day =>
        day.isWorkday && day.location === selectedLocation &&
        (selectedDay ? day.dayOfWeek === selectedDay : true)
      )
    );
  }, [plannedMonth, selectedLocation, selectedDay]);

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-8">
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600"></div>
          <span className="ml-3 text-sm text-gray-500">Werkroosters laden...</span>
        </div>
      </div>
    );
  }

  // Bewust op de ONgefilterde lijst: leeg dóór een filter is iets anders dan
  // "er zijn geen roosters", en heeft hieronder zijn eigen melding.
  if (scopedEmployees.length === 0) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-8">
        <div className="text-center py-8">
          <svg className="mx-auto h-12 w-12 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          <p className="mt-4 text-gray-500">
            {scope === 'team'
              ? 'Er zijn geen teamleden met een werkrooster'
              : 'Er zijn nog geen werkroosters ingevuld'}
          </p>
        </div>
      </div>
    );
  }

  const emptyPlanning = viewMode === 'werkrooster' &&
    ((planningViewMode === 'week' && filteredPlannedWeek.length === 0) ||
     (planningViewMode === 'dag' && employees.length === 0) ||
     (planningViewMode === 'maand' && filteredPlannedMonth.length === 0));

  return (
    <div className="bg-white rounded-lg border border-gray-200">
      {/* Header */}
      <div className="p-4 border-b border-gray-200">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <h2 className="text-lg font-semibold text-gray-900">
            Werkrooster overzicht
          </h2>
          <div className="flex items-center gap-2 flex-wrap">
            <ScopeToggle scope={scope} onScopeChange={setScope} />
            {viewMode === 'werkdagen' && currentWeekType && anyHasWeekB && (
              <span className="text-xs text-gray-400">
                Huidige week: {currentWeekType}
              </span>
            )}
            {viewMode === 'werkdagen' && anyHasWeekB && (
              <WeekToggle
                selectedWeek={effectiveWeek}
                onWeekChange={setSelectedWeek}
                currentWeekType={currentWeekType}
                hasWeekB={anyHasWeekB}
              />
            )}
            <ViewToggle viewMode={viewMode} onViewModeChange={setViewMode} />
          </div>
        </div>

        {viewMode === 'werkrooster' && (
          <div className="flex items-center gap-3 mb-3 flex-wrap">
            <ViewModeSelector viewMode={planningViewMode} onViewModeChange={setPlanningViewMode} />
            <DateNavigator mode={planningViewMode} date={currentDate} onDateChange={setCurrentDate} />
          </div>
        )}

        <ScheduleFilters
          selectedDay={selectedDay}
          selectedLocation={selectedLocation}
          onDayChange={setSelectedDay}
          onLocationChange={setSelectedLocation}
          departments={departments}
          selectedDepartment={effectiveDepartment}
          onDepartmentChange={setSelectedDepartment}
        />
      </div>

      {/* Content */}
      <div className="p-4">
        {viewMode === 'werkdagen' ? (
          filteredEmployees.length === 0 ? (
            <div className="text-center py-6">
              <p className="text-sm text-gray-500">Geen medewerkers gevonden met de huidige filters</p>
            </div>
          ) : (
            <TeamWeekGrid
              employees={filteredEmployees}
              selectedWeek={effectiveWeek}
              filteredDay={selectedDay}
              filteredLocation={selectedLocation}
            />
          )
        ) : emptyPlanning ? (
          <div className="text-center py-6">
            <p className="text-sm text-gray-500">Geen medewerkers gevonden met de huidige filters</p>
          </div>
        ) : planningViewMode === 'week' ? (
          <PlanningWeekGrid
            planned={filteredPlannedWeek}
            weekDates={weekDates}
            filteredDay={selectedDay}
            filteredLocation={selectedLocation}
          />
        ) : planningViewMode === 'dag' ? (
          <PlanningDayView
            planned={plannedWeek}
            date={currentDate}
            filteredLocation={selectedLocation}
          />
        ) : (
          <PlanningMonthGrid
            planned={filteredPlannedMonth}
            workdays={monthWorkdays}
            filteredDay={selectedDay}
            filteredLocation={selectedLocation}
          />
        )}
      </div>
    </div>
  );
}
